import { HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';

import { throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { AUTOSAVE_CONTEXT } from '@app/helpers/autosave-toast.helper';
import { SkipSuccessToastInterceptor, SkipToastInterceptor } from '@app/helpers/skip-toast';
import { ToastHandlerService } from '@app/shared/services/toast-handler.service';

export const toastHandlerInterceptor: HttpInterceptorFn = (request: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const toast = inject(ToastHandlerService);

  const hasSkipToast = request.headers.has(SkipToastInterceptor);
  const hasSkipSuccessToast = request.headers.has(SkipSuccessToastInterceptor);
  const autosaveKey = request.context.get(AUTOSAVE_CONTEXT) as string | undefined;

  let headers = request.headers;

  if (hasSkipToast) {
    headers = request.headers.delete(SkipToastInterceptor).delete(SkipSuccessToastInterceptor);
    if (!autosaveKey) {
      return next(request.clone({ headers }));
    }
  }

  if (hasSkipSuccessToast) {
    headers = request.headers.delete(SkipSuccessToastInterceptor);
  }

  if (autosaveKey) {
    toast.showAutosaveToastResponse(autosaveKey, 'saving');
  }

  return next(request.clone({ headers })).pipe(
    tap({
      next: event => {
        if (event instanceof HttpResponse && autosaveKey) {
          toast.clearToast(`${autosaveKey}-saving`);
          toast.clearToast(`${autosaveKey}-error`);
          toast.showAutosaveToastResponse(autosaveKey, 'success');
        }
      },
      error: () => {
        if (autosaveKey) {
          toast.clearToast(`${autosaveKey}-saving`);
          toast.showAutosaveToastResponse(autosaveKey, 'error');
        }
      },
    }),
    map(event => {
      if (event instanceof HttpResponse && !autosaveKey && !hasSkipToast) {
        if (['PUT', 'DELETE', 'POST', 'PATCH'].includes(request.method) && !request.url.includes('/auth/refresh')) {
          // 204 responses don't have a body.message, so this would throw an error.
          if (`${event.status}`.startsWith('2') && event.status !== 204 && !hasSkipSuccessToast) {
            toast.showSuccessToastsResponse((event.body as { message: string }).message);
          }
        }
      }
      return event;
    }),
    catchError(err => {
      // do not display errors on token expirations and when error contains blob details on file response errors
      if (!autosaveKey && !hasSkipToast && !err.error?.token_expired && !(err.error instanceof Blob)) {
        try {
          toast.showErrorToastsResponse(err);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      }
      return throwError(() => err);
    }),
  );
};